import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ReportToolbar = ({ 
  selectedReport, 
  dateRange, 
  onDateRangeChange, 
  filters, 
  onFiltersChange, 
  onExport, 
  onShare, 
  onPrint,
  onScheduleReport 
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const dateRangePresets = [
    { label: 'Last 7 days', value: 'last-7-days' },
    { label: 'Last 30 days', value: 'last-30-days' },
    { label: 'Last 3 months', value: 'last-3-months' },
    { label: 'Last 6 months', value: 'last-6-months' },
    { label: 'Last year', value: 'last-year' },
    { label: 'Custom range', value: 'custom' }
  ];

  const exportFormats = [
    { label: 'Export as PDF', value: 'pdf', icon: 'FileText' },
    { label: 'Export as Excel', value: 'excel', icon: 'FileSpreadsheet' },
    { label: 'Export as CSV', value: 'csv', icon: 'Database' }
  ];

  const cardStatusOptions = [
    { label: 'All Status', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Restricted', value: 'restricted' },
    { label: 'Expired', value: 'expired' }
  ];

  const userTypeOptions = [
    { label: 'All Users', value: 'all' },
    { label: 'Individual', value: 'individual' },
    { label: 'Team', value: 'team' }
  ];

  const paymentMethodOptions = [
    { label: 'All Methods', value: 'all' },
    { label: 'Credit Card', value: 'credit-card' },
    { label: 'Bank Transfer', value: 'bank-transfer' },
    { label: 'Digital Wallet', value: 'digital-wallet' }
  ];

  const handleFilterChange = (filterType, value) => {
    onFiltersChange({
      ...filters,
      [filterType]: value
    });
  };

  const handleExport = (format) => {
    onExport(format);
    setShowExportMenu(false);
  };

  const getReportTitle = () => {
    if (!selectedReport) return 'Select a Report';
    
    const reportTitles = {
      'card-activity': 'Card Activity Summary',
      'card-performance': 'Card Performance Analysis',
      'card-lifecycle': 'Card Lifecycle Report',
      'card-geographic': 'Geographic Usage Distribution',
      'payment-summary': 'Payment Summary',
      'revenue-analysis': 'Revenue Analysis',
      'payment-methods': 'Payment Methods Report',
      'failed-payments': 'Failed Payments Analysis',
      'user-engagement': 'User Engagement Metrics',
      'user-growth': 'User Growth Analysis',
      'team-performance': 'Team Performance Report',
      'user-retention': 'User Retention Analysis',
      'billing-overview': 'Billing Overview',
      'invoice-analysis': 'Invoice Analysis',
      'subscription-metrics': 'Subscription Metrics',
      'billing-forecasting': 'Billing Forecasting'
    };

    return reportTitles[selectedReport.reportId] || 'Report';
  };

  return (
    <div className="bg-surface border-b border-border">
      {/* Main Toolbar */}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center space-x-4">
          <h1 className="text-xl font-semibold text-text-primary">
            {getReportTitle()}
          </h1>
          
          {selectedReport && (
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                onClick={() => setShowFilters(!showFilters)}
                iconName="Filter"
                iconPosition="left"
                className={showFilters ? 'bg-accent/10 text-accent' : ''}
              >
                Filters
              </Button>
              
              <Button
                variant="ghost"
                onClick={onScheduleReport}
                iconName="Clock"
                iconPosition="left"
              >
                Schedule
              </Button>
            </div>
          )}
        </div>

        {selectedReport && (
          <div className="flex items-center space-x-2">
            {/* Date Range Selector */}
            <select
              value={dateRange.preset}
              onChange={(e) => onDateRangeChange({ ...dateRange, preset: e.target.value })}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
            >
              {dateRangePresets.map(preset => (
                <option key={preset.value} value={preset.value}>
                  {preset.label}
                </option>
              ))}
            </select>

            {/* Action Buttons */}
            <Button
              variant="ghost"
              onClick={onShare}
              iconName="Share2"
              className="p-2"
            />
            
            <Button
              variant="ghost"
              onClick={onPrint}
              iconName="Printer"
              className="p-2"
            />

            {/* Export Dropdown */}
            <div className="relative">
              <Button
                variant="outline"
                onClick={() => setShowExportMenu(!showExportMenu)}
                iconName="Download"
                iconPosition="left"
              >
                Export
              </Button>
              
              {showExportMenu && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-surface border border-border rounded-lg shadow-elevation-3 z-50">
                  <div className="p-2">
                    {exportFormats.map(format => (
                      <button
                        key={format.value}
                        onClick={() => handleExport(format.value)}
                        className="w-full flex items-center px-3 py-2 text-left text-sm text-text-primary hover:bg-hover rounded-lg transition-colors duration-150"
                      >
                        <Icon name={format.icon} size={16} className="mr-3 text-text-muted" />
                        {format.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Advanced Filters */}
      {showFilters && selectedReport && (
        <div className="px-4 pb-4 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            {/* Custom Date Range */}
            {dateRange.preset === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Start Date
                  </label>
                  <Input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => onDateRangeChange({ ...dateRange, startDate: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    End Date
                  </label>
                  <Input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => onDateRangeChange({ ...dateRange, endDate: e.target.value })}
                  />
                </div>
              </>
            )}

            {/* Card Status Filter */}
            {selectedReport.categoryId === 'card-usage' && (
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Card Status
                </label>
                <select
                  value={filters.cardStatus || 'all'}
                  onChange={(e) => handleFilterChange('cardStatus', e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                >
                  {cardStatusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* User Type Filter */}
            {selectedReport.categoryId === 'user-activity' && (
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  User Type
                </label>
                <select
                  value={filters.userType || 'all'}
                  onChange={(e) => handleFilterChange('userType', e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                >
                  {userTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Payment Method Filter */}
            {selectedReport.categoryId === 'payment-analytics' && (
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Payment Method
                </label>
                <select
                  value={filters.paymentMethod || 'all'}
                  onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                >
                  {paymentMethodOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Geographic Region Filter */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Region
              </label>
              <Input
                type="text"
                placeholder="Enter region..."
                value={filters.region || ''}
                onChange={(e) => handleFilterChange('region', e.target.value)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportToolbar;